import type { DensitySnapshot, InstrumentStatusView, WrappedCoin } from "@screener/protocol";
import { Table, TableBody, TableHead, TableRow } from "@screener/uikit/components/table";
import { Fragment, memo, useMemo } from "react";
import { pluralForm } from "@/lib/labels";
import { instrumentKey } from "./cards";
import { CoinNoteIndicator, noteIndicatorFor } from "./coin-note-indicator";
import { buildCoinGroups } from "./coins";
import type { NoteIndicator } from "./density-row";
import { DensityRowItem, rowAnnotations } from "./density-row-item";
import { DensityTableHeader } from "./density-table-header";
import { DENSITY_TABLE_COLUMN_COUNT, type DensitySort, densityEmptyMessage } from "./derive";
import { formatDistance } from "./format";
import type { RowPinMatch } from "./pin-match";
import { useRowNavigation } from "./use-row-navigation";
import { WrappedMark } from "./wrapped-mark";

const EMPTY_PENDING_PIN_IDS: ReadonlySet<string> = new Set();
const EMPTY_PENDING_FAVORITE_BASES: ReadonlySet<string> = new Set();

/**
 * Вид «по монете» §9 (1.21) — одна таблица с общей шапкой (без сортировки
 * заголовками, design.md change stage6-coin-view, решение 2) и строкой-
 * заголовком на группу. Группировка и порядок групп/строк — `buildCoinGroups`
 * (`coins.ts`, решение 1); строка группы — тот же `DensityRow`, что и в
 * таблице, с биржей логотипом (решение 3). Отбор, сортировка экрана и
 * сопоставления заметок/закреплений/избранного приходят уже готовыми — те же,
 * что получает `DensityTable`.
 */

interface DensityCoinTableProps {
  rows: DensitySnapshot[];
  /** Сколько строк было до фильтров — чтобы отличить «не пропустили» от «нет данных». */
  totalRows: number;
  /** Сортировка §9 экрана — тот же порядок, что видят таблица и карточки. */
  sort: DensitySort;
  instruments: InstrumentStatusView[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Приветствие ещё не пришло: вида нет вовсе, а не он пуст. */
  awaitingHello: boolean;
  /** «Только избранные» включён, а список избранного ещё не прочитан или недоступен. */
  favoritesUnavailable: boolean;
  /** Открыть детальную панель §9 по строке. */
  onOpenRow: (row: DensitySnapshot) => void;
  /** Монеты с заметками §6.4 — то же сопоставление клиента, что и у `DensityTable`. */
  notedBases: Set<string> | null;
  /** Закрепления §6.7, сопоставленные со строками — то же, что и у `DensityTable`. */
  pinMatches: Map<string, RowPinMatch> | null;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: (row: DensitySnapshot) => void;
  /** Закрепить уровень строки или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Строки, чей собственный запрос закрепления ещё не завершился. */
  pendingPinIds?: ReadonlySet<string>;
  /** Монеты, отмеченные избранным целиком §6.6 — то же, что и у `DensityTable`. */
  favoriteBases: Set<string> | null;
  /** Поставить или снять избранное монеты строки целиком. */
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Монеты, чьё собственное действие избранного ещё не завершилось. */
  pendingFavoriteBases?: ReadonlySet<string>;
  /** Скрыть монету целиком — хоткей `h` строки (§75 п. 2). */
  onHideCoin: (base: string) => void;
  /** Скопировать тикер строки в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: (row: DensitySnapshot) => void;
}

interface CoinGroupHeaderProps {
  base: string;
  count: number;
  nearestDistancePct: number | null;
  hasNote: NoteIndicator;
  wrapped: readonly WrappedCoin[];
}

/**
 * Шапка группы монеты. Все пропсы — значения, поэтому `memo` с проверкой по
 * умолчанию перерисовывает её, лишь когда изменились состав группы, ближайшая
 * дистанция или заметка, а не от каждого среза соседних плотностей.
 */
const CoinGroupHeader = memo(function CoinGroupHeader({
  base,
  count,
  nearestDistancePct,
  hasNote,
  wrapped,
}: CoinGroupHeaderProps) {
  return (
    <TableRow data-coin-group={base} className="h-7 bg-muted/40 hover:bg-muted/40">
      {/* `<th scope="rowgroup">`, а не `<td>`: строка заголовка
          называет группу строк под собой, а не несёт величину
          своей колонки — та же табличная семантика, что и у
          заголовка колонок сверху. */}
      <TableHead
        scope="rowgroup"
        colSpan={DENSITY_TABLE_COLUMN_COUNT}
        className="h-7 px-2 font-normal"
      >
        <span className="flex items-center gap-2 text-[12px]">
          <span className="font-semibold">
            {base}
            <WrappedMark base={base} wrapped={wrapped} className="ml-1.5" />
          </span>
          <span className="text-muted-foreground">
            {count} {pluralForm(count, "плотность", "плотности", "плотностей")}
          </span>
          <span className="text-muted-foreground">{formatDistance(nearestDistancePct)}</span>
          <CoinNoteIndicator base={base} hasNote={hasNote} />
        </span>
      </TableHead>
    </TableRow>
  );
});

export function DensityCoinTable({
  rows,
  totalRows,
  sort,
  instruments,
  wrapped,
  awaitingHello,
  favoritesUnavailable,
  onOpenRow,
  notedBases,
  pinMatches,
  onOpenNotes,
  onTogglePin,
  pendingPinIds = EMPTY_PENDING_PIN_IDS,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases = EMPTY_PENDING_FAVORITE_BASES,
  onHideCoin,
  onCopyTicker,
}: DensityCoinTableProps) {
  const groups = useMemo(() => buildCoinGroups(rows, sort), [rows, sort]);
  // Плоский список `density_id` всех групп — заголовки групп в него не
  // входят (design.md, решение 6): стрелки обязаны их пропускать, заголовок —
  // не плотность, и действий у него нет.
  const keys = useMemo(
    () => groups.flatMap((group) => group.rows.map((row) => row.density_id)),
    [groups],
  );
  // Шаг `"keys"`, как у карточек: между последней строкой одной группы и
  // первой строкой следующей нет общего родителя в DOM — между ними стоит
  // строка-заголовок.
  const { focusable, onFocus, onKeyDown } = useRowNavigation(keys, { step: "keys" });

  // Состав устаревших инструментов меняется со статусом, а не с каждым кадром.
  const staleInstruments = useMemo(
    () =>
      new Set(
        instruments
          .filter((entry) => entry.status !== "live")
          .map((entry) => instrumentKey(entry.instrument)),
      ),
    [instruments],
  );
  const annotationSources = {
    notedBases,
    pinMatches,
    pendingPinIds,
    favoriteBases,
    pendingFavoriteBases,
  };

  const emptyMessage = densityEmptyMessage({ awaitingHello, totalRows, favoritesUnavailable });

  return (
    <div data-row-nav>
      <Table className="text-[13px]">
        <DensityTableHeader showsSort={false} />

        <TableBody>
          {groups.length === 0
            ? null
            : groups.map((group) => (
                <Fragment key={group.base}>
                  <CoinGroupHeader
                    base={group.base}
                    count={group.rows.length}
                    nearestDistancePct={group.nearestDistancePct}
                    hasNote={noteIndicatorFor(group.base, notedBases)}
                    wrapped={wrapped}
                  />
                  {group.rows.map((row) => (
                    <DensityRowItem
                      key={row.density_id}
                      row={row}
                      wrapped={wrapped}
                      stale={staleInstruments.has(instrumentKey(row.instrument))}
                      focusable={focusable(row.density_id)}
                      {...rowAnnotations(row, annotationSources)}
                      onFocus={onFocus}
                      onKeyDown={onKeyDown}
                      onOpenRow={onOpenRow}
                      onOpenNotes={onOpenNotes}
                      onTogglePin={onTogglePin}
                      onToggleFavorite={onToggleFavorite}
                      onHideCoin={onHideCoin}
                      onCopyTicker={onCopyTicker}
                    />
                  ))}
                </Fragment>
              ))}
        </TableBody>
      </Table>
      {/* Пустое состояние — вне горизонтально прокручиваемой таблицы: иначе оно
          центрируется по её полной ширине и на узком окне уходит за край. */}
      {groups.length === 0 ? (
        <p className="flex min-h-20 items-center justify-center px-4 py-3 text-center text-[13px] text-balance text-muted-foreground">
          {emptyMessage}
        </p>
      ) : null}
    </div>
  );
}
